/*
    Copyright (C) Paul B. Lucas 2025, 2026  ALL RIGHTS RESERVED
    Licensed to the University of Illinois Urbana Champaign

    This is a Javascript composable that uses the Microsoft GraphAPI to perform
    user lookups, user group validation and group membership changes.
    
    Methods incuded:

        getOIDOnUserPrincipalName()
        getUserGroups()
        setGroupMembers()
        addMemberToGroup()
        removeMemberFromGroup()

        graphError is set on any error
     

    These are async functions that awaits a results of an axios call to the Graph API. When promise 
    is resolved, the results are returned in results ref.  All access is delegated access so the current application
    user must be authenticated and the graph access token obtained from the authentication results.

        getOIDOnUserPrincipalName(token, userPrincipalName) returns user directory object with specific $selects
        (id, displayName, userPrincipalName (email)). Returns:
        {
            "@odata.context": "https://graph.microsoft.com/v1.0/$metadata#users/$entity",
            "displayName": "Stark, Anthony E",
            "userPrincipalName": "ironman0@illinois.edu",
            "id": "9c98a6a7-541a-4d84-949b-7e1eba1ec1d6"
        }

        getUserGroups(token) return json data is the following format:
        {
            "@odata.context": "https://graph.microsoft.com/v1.0/$metadata#groups(displayName,id)",
            "@odata.count": 91,
            "value": [
                {
                    "displayName": "CITES SP Business Office AS Owners",
                    "id": "00979257-7163-4fc0-a2bf-a3990afcae68"
                },
                {
                    "displayName": "CITES SP Facilities",
                    "id": "012f900a-ffd7-4bd2-979b-eccf3071a21b"
                },
                . . .
            ]
        }    
        the token argument is the returned OAuth token from the user's authentication call and
        is set as header('Authorization: bearer <token>').  Additionally, a content-type: application/json
        header is set on the request.

    API configuration data is stored as JSON file in src/configuration/ and imported as a JSON array.  Imports
    can do this from a basic JSON file.  Parameters in the config array include the base-url, 
    endpoints, and authentication data for the axios headers. 

    
    <Revision History>
        08/29/2024      pblucas     created 
        09/17/2024      pblucas     Final testing of add/remove members from groups
        06/17/2025      pblucas     added status response to return refs
        03/16/2026      pblucas     Corrected typos & updated copyright    

    </Revision History>
*/

import { ref } from 'vue'
import axios from 'axios'
import GraphApiConfigs from '@/configuration/graphAPI.config.json'

const useGraphAPI = () =>    {

    // set specific endpoint for directory objects
    const baseURL       =   GraphApiConfigs['base-url']
    const APIVers       =   GraphApiConfigs['graph-version']
    const dirObject     =   GraphApiConfigs['directory-object']
    const Groups        =   GraphApiConfigs['groups']
    const Members       =   GraphApiConfigs['members']
    const URIMeGroups   =   GraphApiConfigs['me-groups-path']
    const users         =   GraphApiConfigs['users']
    const oidPostBody   =   GraphApiConfigs['oid-post-body']
    const membersRef    =   GraphApiConfigs['members-ref']
    const QuerySearch   =   GraphApiConfigs['QSSearch']
      
    // result reference to return assets: array
    const groupResults          =   ref({}) 
    const addMemberResults      =   ref({})   
    const removeMemberResults   =   ref({})  
    const userResults           =   ref({})   
    const graphError            =   ref(false)
    const statusCode            =   ref(200)        // assume 'OK'
 
    


    const  getUserNetidOnDisplayName = async (token, displayName)  =>  {
          
        graphError.value = false
        let url =  baseURL + APIVers +  users + QuerySearch +  '"displayName:' + displayName.trim() + '"'
        await axios({  
            method:         'get',
            url:            url,
            headers:        {Authorization: `${GraphApiConfigs['auth-key-name']} ${token}`,
                             ConsistencyLevel: 'eventual'}
        })
        .then ( (response) => {   
            //console.log("usegraphAPI:: getUserNetidOnDisplayName: response: "+JSON.stringify(response.data))
            let userPrincipleName = response.data.value[0].userPrincipalName        // email
            let aParts = userPrincipleName.split('@')
            userResults.value      =   aParts[0]                                    // netid  
            statusCode.value = response?.status
        })
        .catch( error =>  {
            //console.log("Graph:: "+ displayName+": error: "+error.message)
            console.log("Graph:: "+ displayName+" is not a current university employee.")
            userResults.value = "employee not found"
            graphError.value = true
            statusCode.value = error?.response?.status     
        })   
    }   // end function

    const  getOIDOnUserPrincipalName = async (token, principalName)  =>  {
         
        graphError.value = false
        let url =  baseURL + APIVers +  users + principalName   
        await axios({  
            method:         'get',
            url:            url,
            headers:        {Authorization: `${GraphApiConfigs['auth-key-name']} ${token}`,
                             ConsistencyLevel: 'eventual'}
        })
        .then ( (response) => {   
             userResults.value      =   response.data     
             statusCode.value = response?.status       
        })
        .catch( error =>  {
            //console.log("Graph:: "+ error.message)
            userResults.value = []
            graphError.value = true
            statusCode.value = error?.response?.status  
        })
          
    }   // end function


    const  getOIDOnUserDisplayName = async (token, displayName)  =>  {
         
        graphError.value = false
        let url =  baseURL + APIVers +  users + QuerySearch +  '"displayName:' + "'"+displayName.trim() +"'" + '"'  

        await axios({  
            method:         'get',
            url:            url,
            headers:        {Authorization: `${GraphApiConfigs['auth-key-name']} ${token}`,
                             ConsistencyLevel: 'eventual'}
        })
        .then ( (response) => {   
             userResults.value      =   response.data   
             statusCode.value = response?.status             
        })
        .catch( error =>  {
            console.log("Graph:: "+ error.message)
            userResults.value = []
            graphError.value = true
            statusCode.value = error?.response?.status     
        })   
    }   // end function

    const  getUserGroups = async (token)  =>  {   
        graphError.value = false
        let url =  baseURL + APIVers +  URIMeGroups   
        await axios({  
            method:         'get',
            url:            url,
            headers:        {Authorization: `${GraphApiConfigs['auth-key-name']} ${token}`}
        })
        .then ( (response) => {   
            groupResults.value      =   response.data.value   
            statusCode.value = response?.status          
        })
        .catch( error =>  {
            console.log("Graph:: "+ error.message)
            groupResults.value = []
            graphError.value = true
            statusCode.value = error?.response?.status    
        })   
    }   // end function

    // no graph permissions for User.Read.All
    const  getGroupMembers = async (token, groupId)  =>  {
        graphError.value = false
        let url =  baseURL + APIVers +  Groups + groupId + Members   
        await axios({  
                method:         'get',
                url:            url,
                headers:        {Authorization: `${GraphApiConfigs['auth-key-name']} ${token}`}
        })
        .then ( (response) => {   
            groupResults.value      =   response.data.value   
            statusCode.value = response?.status     
            return(response.data.value);
        })      
        .catch( error =>  {
            console.log("Graph:: "+ error.message)
            groupResults.value = []
            graphError.value = true
            statusCode.value = error?.response?.status   
        })        
    }   // end function


    // MARK:  Mutates Entra group objects via graph
    const  addMemberToGroup = async (token, emplOID, groupId)  =>  {
        graphError.value = false
        var payload = {'@odata.id': baseURL+APIVers+dirObject+emplOID}

        let url =  baseURL + APIVers +  Groups + groupId + membersRef 
        await axios({
            method: 'POST',
            url: url,
            data: payload,
            headers: {'Content-Type': 'application/json',
                        'Authorization': `${GraphApiConfigs['auth-key-name']} ${token}`}
        })
        .then((response) => {
            addMemberResults.value = response.data.value
            statusCode.value = response?.status   
        })
        .catch( error =>  {
            console.log("Graph:: "+ error.message)
            graphError.value = true
            statusCode.value = error?.response?.status   
        })   
    }   // end function

    const  removeMemberFromGroup = async (token, emplOID, groupId)  =>  {
        graphError.value = false
        let url =  baseURL + APIVers +  Groups + groupId + "/members/" + emplOID + "/$ref" 
        await axios({
            method: 'DELETE',
            url: url,
            headers: {Authorization: `${GraphApiConfigs['auth-key-name']} ${token}`}
        })
        .then((response) => {
            removeMemberResults.value = response.data.value
            statusCode.value = response?.status   
        })
        .catch( error =>  {
            console.log("Graph:: "+ error.message)
            graphError.value = true
            statusCode.value = error?.response?.status  
        })   
    }   // end function

    return {  userResults, groupResults, addMemberResults, removeMemberResults, getGroupMembers, 
              getUserGroups, getOIDOnUserPrincipalName, getUserNetidOnDisplayName, getOIDOnUserDisplayName,
              addMemberToGroup, removeMemberFromGroup, graphError, statusCode
           }
}

export default useGraphAPI